import { AlertCircle, ChevronRight } from "lucide-react";

import { ScoreVerdict } from "@/queries/smart-tables/scorecard";

export interface ScorecardCriterionIssue {
  stepId: string;
  title: string;
  verdict: string;
  reason: string;
}

interface ScorecardCriterionErrorsBannerProps {
  issues: ScorecardCriterionIssue[];
  onOpenCriterion: (stepId: string) => void;
  onOpenIssue?: (issue: ScorecardCriterionIssue) => void;
}

const getHeaderLabel = (issues: ScorecardCriterionIssue[]): string => {
  const errorCount = issues.filter(issue => issue.verdict === ScoreVerdict.ERROR).length;
  const failCount = issues.length - errorCount;

  if (errorCount > 0 && failCount > 0) {
    return `${issues.length} evaluators need attention`;
  }
  if (errorCount > 0) {
    return errorCount === 1
      ? "1 evaluator failed to evaluate"
      : `${errorCount} evaluators failed to evaluate`;
  }
  return failCount === 1 ? "1 evaluator is failing" : `${failCount} evaluators are failing`;
};

const ScorecardCriterionErrorsBanner = ({
  issues,
  onOpenCriterion,
  onOpenIssue,
}: ScorecardCriterionErrorsBannerProps) => {
  if (issues.length === 0) {
    return null;
  }

  return (
    <div className="overflow-hidden rounded-lg border border-red-200 bg-red-50">
      <div className="flex items-start gap-1.5 px-3 py-2 text-xs font-medium leading-5 text-red-900">
        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1">{getHeaderLabel(issues)}</span>
      </div>
      <div className="max-h-48 overflow-y-auto border-t border-red-200/80">
        {issues.map(issue => (
          <button
            key={issue.stepId}
            type="button"
            onClick={() => (onOpenIssue ? onOpenIssue(issue) : onOpenCriterion(issue.stepId))}
            className="flex w-full items-start justify-between gap-2 border-b border-red-200/60 px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-red-100/60"
          >
            <span className="min-w-0 flex-1 space-y-0.5">
              <span className="block truncate text-xs font-medium leading-5 text-red-900">
                {issue.title}
              </span>
              <span className="line-clamp-2 block break-words text-[11px] leading-4 text-red-700">
                {issue.reason}
              </span>
            </span>
            <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-700" />
          </button>
        ))}
      </div>
    </div>
  );
};

export default ScorecardCriterionErrorsBanner;
